import { Calendar } from '@/components/calendar/Calendar';
import { MonthPicker } from '@/components/shared/date/MonthPicker';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { dateToApiFormat, getMonthBounds } from '@/helpers/dateHelper';
import { useEmployeeSchedule } from '@/services/employee/schedule/useEmployeeSchedule';
import { FC, Suspense, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TeamMemberCalendarContent } from './TeamMemberCalendarContent';
import { useTeamMemberCalendarEvents } from './useTeamMemberCalendarEvents';

interface TeamMemberCalendarProps {
  selectedDate: Date;
  setSelectedDate: (newDate: Date) => void;
}

export const calendarSectionId = 'calendarSectionId';

export const TeamMemberCalendar: FC<TeamMemberCalendarProps> = ({ selectedDate, setSelectedDate }) => {
  const { userId } = useParams<'userId'>();

  if (!userId) {
    throw Error('User id should be provided in url params');
  }

  const [startDate, endDate] = useMemo(() => {
    return getMonthBounds(selectedDate);
  }, [selectedDate]);

  const data = useEmployeeSchedule(userId, startDate, endDate);

  const navigate = useNavigate();

  const onDayClick = (date: Date) => {
    navigate(`/team-member/${userId}/calendar-details/${dateToApiFormat(date)}`);
  };

  const events = useTeamMemberCalendarEvents(data.schedule, onDayClick);

  return (
    <section id={calendarSectionId} className="space-y-5">
      <MonthPicker value={selectedDate} onChange={setSelectedDate} />
      <Suspense fallback={<TableLoader />}>
        <Calendar
          monthToShow={startDate}
          events={events}
          ContentComponent={TeamMemberCalendarContent}
          options={{ minimumCellHeight: 115, hideOverflow: true }}
        />
      </Suspense>
    </section>
  );
};
